import React from 'react';
import { Zap, CheckCircle, Sparkles, Bookmark, Code } from 'lucide-react';

// Real Facebook Logo SVG
const FacebookLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Real WhatsApp Logo SVG
const WhatsAppLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const HeroSection = ({ onBookNow }) => {
  const whatsappNumber = "+447471343190";
  const whatsappUrl = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=Hi%20NEURA%20AI!%20I'm%20interested%20in%20your%20services.`;

  const handleAddToSources = () => {
    // Check if browser supports adding to bookmarks/sources
    if (window.sidebar && window.sidebar.addPanel) {
      // Firefox
      window.sidebar.addPanel('NEURA AI SOFTWARES', window.location.href, '');
    } else if (window.external && 'AddFavorite' in window.external) {
      // IE
      window.external.AddFavorite(window.location.href, 'NEURA AI SOFTWARES');
    } else {
      // Modern browsers - show instruction
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
      alert(`Press ${shortcut} to bookmark this page to your preferred sources!`);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Premium CSS Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        
        @keyframes flashBlueWhite {
          0%, 100% { background-color: #1877F2; color: white; }
          50% { background-color: white; color: #1877F2; border-color: #1877F2; }
        }
        
        .flash-blue-white {
          animation: flashBlueWhite 1.5s ease-in-out infinite;
          border: 2px solid #1877F2;
        }
        
        .hero-headline {
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1.05;
        }
        
        .text-gradient-premium {
          background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .text-gradient-accent {
          background: linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #c026d3 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }
        
        .btn-4dx {
          background: linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%);
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        
        .btn-4dx::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          background-size: 200% 100%;
          animation: shimmer 3s infinite;
        }
        
        .btn-4dx:hover {
          transform: translateY(-2px);
          box-shadow: 0 20px 40px -10px rgba(6, 182, 212, 0.5);
        }
        
        .trust-badge {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.1);
          backdrop-filter: blur(8px);
          transition: all 0.3s ease;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
        }
        
        .trust-badge:hover {
          background: rgba(255, 255, 255, 1);
          border-color: rgba(37, 99, 235, 0.3);
          transform: translateY(-2px);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      {/* Blue to White Gradient Background (30% blue, 70% white) */}
      <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-blue-600 via-blue-200 via-30% to-white">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 text-center pt-32 sm:pt-24 lg:pt-20">
        
        {/* Announcement Badge */}
        <div className="inline-flex items-center gap-2 glass-card px-4 py-2 rounded-full mb-8">
          <Sparkles className="w-4 h-4 text-blue-600" />
          <span className="text-blue-700 text-sm font-medium">INCOME Growth SOFTWARES for Business</span>
        </div>
        
        {/* Main Headline */}
        <h1 className="hero-headline text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mb-6">
          <span className="text-gradient-premium">Turn Missed Enquiries Into</span>
          <br />
          <span className="text-gradient-accent">Booked Customers — Automatically.</span>
        </h1>

        {/* Subheadline */}
        <p className="text-slate-600 text-lg sm:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
          We build AI sales systems for UK SMEs that 
          <span className="text-blue-600 font-semibold"> respond to enquiries, qualify leads, follow up and book appointments 24/7</span>.
        </p>

        {/* Social Icons - Facebook & WhatsApp */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <a 
            href="https://www.facebook.com/share/1VdU7oUHsS/?mibextid=wwXIfr"
            target="_blank"
            rel="noopener noreferrer"
            className="flash-blue-white flex items-center gap-2 px-5 py-3 rounded-full font-semibold transition-transform hover:scale-105"
            data-testid="facebook-btn"
          >
            <FacebookLogo className="w-5 h-5" />
            <span className="text-sm">Follow Us</span>
          </a>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-5 py-3 rounded-full font-semibold bg-[#25D366] text-white border-2 border-[#25D366] transition-transform hover:scale-105 hover:bg-[#20BD5A]"
            data-testid="whatsapp-btn"
          >
            <WhatsAppLogo className="w-5 h-5" />
            <span className="text-sm">WhatsApp</span>
          </a>
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-10">
          <div className="trust-badge flex items-center gap-2 px-4 py-2.5 rounded-full">
            <Code className="w-4 h-4 text-purple-600" />
            <span className="text-slate-700 text-sm font-medium">Custom Softwares</span>
          </div>
          <div className="trust-badge flex items-center gap-2 px-4 py-2.5 rounded-full">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-slate-700 text-sm font-medium">Free AI Audit</span>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center mb-10">
          <button
            className="w-full sm:w-auto btn-4dx text-white font-semibold px-8 py-4 rounded-full"
            onClick={() => document.querySelector('#packages')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="hero-cta-audit"
          >
            <span className="relative z-10 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Get Your Free AI Revenue Audit
            </span>
          </button>
        </div>

        {/* Social Proof */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          <div className="glass-card px-6 py-3 rounded-xl">
            <p className="text-3xl font-bold text-slate-800">100+</p>
            <p className="text-slate-500 text-sm">UK SMEs Served</p>
          </div>
          <div className="glass-card px-6 py-3 rounded-xl">
            <p className="text-3xl font-bold text-blue-600">24/7</p>
            <p className="text-slate-500 text-sm">Lead Response</p>
          </div>
          <div className="glass-card px-6 py-3 rounded-xl">
            <p className="text-3xl font-bold text-purple-600">Belfast</p>
            <p className="text-slate-500 text-sm">Based Team</p>
          </div>
        </div>

        {/* Add to Preferred Sources */}
        <div className="mt-8">
          <button
            onClick={handleAddToSources}
            className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 text-sm font-medium transition-colors"
            data-testid="add-to-sources-btn"
          >
            <Bookmark className="w-4 h-4" />
            Add to Preferred Sources
          </button>
        </div>
      </div>

      {/* Bottom Gradient Fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-100 to-transparent z-[2]" />
    </section>
  );
};

export default HeroSection;
